/* eslint-disable react/no-danger */
import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { Typography } from '@material-ui/core';
import useQlikSessionObject from 'hooks/useQlikSessionObject';
import useStyles from './styles';
import Pie from '../Charts/Pie';
import languagify from '../../../utils/languagify';

import AppContext from '../../AppContext';

import TranslationTxt from '../TranslationTxt/TranslationTxt';

const PieSection = ({ header, text }) => {
  const { qlikApp } = useContext(AppContext);

  const classes = useStyles();

  const dataOptions = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qHyperCubeDef: {
        qDimensions: [],
        qMeasures: [
          {
            qDef: {
              qDef: "=(Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'0'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023])/count( {<[Name of media outlet for search - 2023]=>} [Name of media outlet completing the survey - 2023])) * 100",
              qNumFormat: {
                qType: 'I',
                qnDec: 2,
                qUseThou: 0,
                qFmt: '####',
                qDec: '.',
              },
            },
          },
          {
            qDef: {
              qDef: "=((Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'1'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'0'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023])+Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'1'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'0'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023])+Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'1'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023]))/count({<[Name of media outlet for search - 2023]=>}  [Name of media outlet completing the survey - 2023])) * 100",
              qNumFormat: {
                qType: 'I',
                qnDec: 2,
                qUseThou: 0,
                qFmt: '####',
                qDec: '.',
              },
            },
          },
          {
            qDef: {
              qDef: "=(1-(Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'0'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023])/Count({<[Name of media outlet for search - 2023]=>}  [Name of media outlet completing the survey - 2023])+(Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'1'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'0'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023])+Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'1'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'0'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023])+Count( {<[Top 3 Supervisor - Visible Minorities or Indigenous - Men - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Women - 2023]={'0'},[Top 3 Supervisor - Visible Minorities or Indigenous - Non-Binary - 2023]={'1'}, [Name of media outlet for search - 2023]=>}[Name of media outlet completing the survey - 2023]))/count({<[Name of media outlet for search - 2023]=>}  [Name of media outlet completing the survey - 2023]))) * 100",
              qNumFormat: {
                qType: 'I',
                qnDec: 2,
                qUseThou: 0,
                qFmt: '###',
                qDec: '.',
              },
            },
          },
        ],
      },
    },
  });

  const _kpis = useMemo(() => {
    if (!dataOptions.layout) return [];
    return dataOptions.layout.qHyperCube.qDataPages[0].qMatrix.map((row) => [
      {
        text: '=Only({<Index = {57}>}[$(vLanguage)])',
        number: row[0].qText,
        color: '#75798E',
      },
      {
        text: '=Only({<Index = {58}>}[$(vLanguage)])',
        number: row[1].qText,
        color: '#D9A798',
      },
      {
        text: '=Only({<Index = {59}>}[$(vLanguage)])',
        number: row[2].qText,
        color: '#95CFC2',
      },
    ])[0];
  }, [dataOptions.layout]);

  return (
    <div className="section" style={{ marginTop: 50 }}>
      <Typography variant="h3" className={classes.header}>
        <TranslationTxt measure={header} />
      </Typography>
      <Typography variant="body1" className={classes.text}>
        <TranslationTxt measure={text} />
      </Typography>
      <Box className={classes.kpis}>
        {_kpis.map((kpi) => (
          <Box className={classes.kpi} key={kpi.number}>
            <Typography variant="body2" className={classes.kpiNumber} style={{ color: kpi.color }} dangerouslySetInnerHTML={{ __html: `${kpi.number}<span style="font-size: 16px; position: relative; top: -4px;">%</span>` }} />
            <Typography variant="body1" className={classes.kpiText}>
              {languagify('OF NEWSROOMS HAVE')}
              <br />
              <TranslationTxt measure={kpi.text} />
              <br />
              {languagify('IN A TOP LEADERSHIP ROLE')}
            </Typography>
            <div className={classes.divider} style={{ backgroundColor: kpi.color }} />
          </Box>
        ))}

      </Box>
      <Box className={classes.chartsWrapper}>
        <Typography variant="body1" className={classes.chartTitle}>
          <TranslationTxt measure="=Only({<Index = {56}>}[$(vLanguage)])" />
        </Typography>
        <Box className={classes.chartsContainer}>
          <Pie />
        </Box>
      </Box>

    </div>
  );
};

PieSection.propTypes = {
  header: PropTypes.string,
  text: PropTypes.string,
};

PieSection.defaultProps = {
  header: '',
  text: '',
};

export default PieSection;
